import moment from 'moment';
import uuid from 'uuid';
import { FlightRequestLeg } from 'acscp/api/models/flightRequestLeg';
import { FlightRequest } from 'acscp/api/models/flightRequest';
import { Airport, Booking, BookingLeg } from 'acscp/store';
import { tripTypeFromLegs } from './tripTypeHelper';
import { defaultLeg } from './defaultLeg';
import { TripType } from './tripType';
import { isoDateFormat, isoTimeFormat } from 'acscp/store/dateTime';

// This does not need to be localised as it is in the HTML spec
export const datePickerFormat = isoDateFormat;
export const timePickerFormat = isoTimeFormat;

export interface FormFlightRequest {
    tripType: TripType;
    legs: FlightRequestFormLeg[];
    customerId: string;
    originalBookingFriendlyId: string | null;
}

export interface FlightRequestFormLeg {
    id: string;
    departureAirport: Airport | null;
    departureLocation: string | null;
    arrivalAirport: Airport | null;
    arrivalLocation: string | null;
    date: string; // Should be a string in YYYY-MM-DD format
    useDepartureDate: boolean;
    time: string; // Should be a string in HH:mm format
    passengers: number;
    specialRequests: string;
    isExpanded: boolean;
    showValidationErrors: boolean;
}

export function mapFormFlightRequestToFlightRequest(formFlightRequest: FormFlightRequest): FlightRequest {
    const legs = formFlightRequest.legs.map(mapFlightRequestFormLegToLeg);
    return {
        legs,
        customerId: formFlightRequest.customerId,
        originalBookingFriendlyId: formFlightRequest.originalBookingFriendlyId
    };
}

export function mapFlightRequestFormLegToLeg(formLeg: FlightRequestFormLeg): FlightRequestLeg {
    const formLegDateString =  moment.utc(formLeg.date, datePickerFormat).format(isoDateFormat);
    const formLegTimeString = !!formLeg.time ? moment.utc(formLeg.time, timePickerFormat).format(isoTimeFormat) : null;
    const {departureDate, departureTime, arrivalDate, arrivalTime}
        = formLeg.useDepartureDate
        ? {
            departureDate: formLegDateString,
            departureTime: formLegTimeString,
            arrivalDate: null,
            arrivalTime: null
        }
        : {
            departureDate: null,
            departureTime: null,
            arrivalDate: formLegDateString,
            arrivalTime: formLegTimeString
        };

    return {
        numberOfPassengers: formLeg.passengers,
        specialRequests: formLeg.specialRequests,
        departureDate,
        departureTime,
        departFromAirport: formLeg.departureAirport !== null,
        departureAirportId: formLeg.departureAirport && formLeg.departureAirport.id,
        departureOtherLocation: formLeg.departureLocation,
        arrivalDate,
        arrivalTime,
        arriveAtAirport: formLeg.arrivalAirport !== null,
        arrivalAirportId: formLeg.arrivalAirport && formLeg.arrivalAirport.id,
        arrivalOtherLocation: formLeg.arrivalLocation,
        useArrivalDate: !formLeg.useDepartureDate,
    };
}

export function mapBookingToFormFlightRequest(booking: Booking, isRepeatRequest: boolean): FormFlightRequest {
    const legs = isRepeatRequest
        ? booking.legs.map(mapBookingLegToFlightRequestFormLegForRepeatRequest)
        : booking.legs.map(mapBookingLegToFlightRequestFormLeg);
    return {
        tripType: tripTypeFromLegs(legs),
        legs,
        customerId: booking.customerId,
        originalBookingFriendlyId: isRepeatRequest ? booking.enquiryId : null
    };
}

function getAirport(id: string | null, name: string): Airport | null {
    if (id === null) {
        return null;
    }

    return {
        id,
        name,
        iataCode: null,
        icaoCode: null,
        town: null
    };
}

function mapBookingLegToFlightRequestFormLeg(leg: BookingLeg): FlightRequestFormLeg {
    // The time of the departure will be left null if the user has instead set the arrival
    const useDepartureDate = leg.departureDate.hasTime() >= leg.arrivalDate.hasTime();

    return {
        ...getBasicFlightRequestLegFromBookingLeg(leg),
        date: useDepartureDate ? leg.departureDate.format(datePickerFormat) : leg.arrivalDate.format(datePickerFormat),
        // Use HH:mm time format regardless of locale - time will be coverted to new format for display later if necessary
        time: (useDepartureDate ? leg.departureDate.getLocallyFormattedTime() : leg.arrivalDate.getLocallyFormattedTime()) || '',
        useDepartureDate,
        isExpanded: false,
        showValidationErrors: false
    };
}

function mapBookingLegToFlightRequestFormLegForRepeatRequest(leg: BookingLeg): FlightRequestFormLeg {
    const repeatLeg = defaultLeg();
    Object.assign(repeatLeg, getBasicFlightRequestLegFromBookingLeg(leg));
    return repeatLeg;
}

const getBasicFlightRequestLegFromBookingLeg = (leg: BookingLeg) => {
    return{
        id: uuid.v4(),
        departureAirport: getAirport(leg.departureAirportId, leg.departureLocation),
        departureLocation: leg.departureLocation,
        arrivalAirport: getAirport(leg.arrivalAirportId, leg.arrivalLocation),
        arrivalLocation: leg.arrivalLocation,
        passengers: leg.numberOfPassengers || 0,
        specialRequests: leg.specialRequests || '',
    };
};
